import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core';

import React from 'react';

import { baseTheme } from '../theme';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      paddingLeft: theme.spacing(4),

    },
    spacer: {
      paddingTop: theme.spacing(4),
    },
    indentSpacer: {
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
    },
  }),
);

export default function PrivacyPolicyPage() {
  const classes = useStyles(baseTheme);
  return (
    <Grid item container direction="column" className={classes.root}>
      <Typography variant="h4" color="primary">
        Privacy Policy
      </Typography>
      <Typography>
        Date of last revision: March 10, 2022
      </Typography>

      <Typography className={classes.spacer}>
        Thanks for using Reliht. This policy explains the what, how, and why of the information we collect when you use Reliht.
        It also explains the specific ways we use and disclose that information. <b>We never sell lists or email addresses.</b> By
        using this Site, you are accepting the practices described in this Privacy Policy.
      </Typography>

      <Typography variant="h5" color="primary" className={classes.spacer}>
        Basic Information
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Definitions
        <Typography className={classes.indentSpacer}>
          When we say "we", "our", "us", and "Reliht", we're referring to the Reliht web and mobile apps, owned by Isaiah 601, Inc., a corporation based in Richfield, Minnesota USA. When we say "you", “Leader”, “Individual”, "User" or “Member”, we're referring to the leader, church, ministry, group or person registered with us to use the Services. Any other defined (capitalized) terms not otherwise defined herein have the same meaning as in our Terms and Conditions. Reliht is a registered Trademark and a registered assumed name of Isaiah 601, Inc., a Minnesota corporation.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Changes
        <Typography className={classes.indentSpacer}>
          If there are any changes to this Privacy Policy in regards to how we handle Member's personal information, we will post them on the Website. Any changes will be effective immediately. If you object to any changes we make you may always cancel your account at any time pursuant to the process outlined in our Terms and Conditions. It is your responsibility to periodically check the Site to determine if there have been changes made and to review such changes.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Questions
        <Typography className={classes.indentSpacer}>
          If you have any questions or comments, or if you want to update, delete, or change any Personal Information you've submitted on the Website or App, please go to the accounts page or fill out our <a href="/connect">connect</a> form.
        </Typography>
      </Typography>

      <Typography variant="h5" color="primary" className={classes.spacer}>
        Your Personal Information
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Information We Collect
        <Typography className={classes.indentSpacer}>
          When you register and create an account at https://www.Reliht.com (either "Leader" or "Individual" accounts) or through the mobile application (the App) you will be asked to provide certain Personal Information including your first name, last name, and email address. We may ask for additional Personal Information that will not be required (optional) such as age, gender, and geographic location to give us a better understanding of our users.
          <br />
          <b>Information from your Use of the Service:</b> We may get information about how and when you use the Services. This information may include your IP address, time, date, browser used, and actions taken by you within the application.
          <br />
          <b>Cookies:</b> When you register to use Reliht, we store "cookies," which are strings of code, on your computer. We use those cookies to collect information about when you visit our Website, when you use the Services, your browser type and version, your operating system, and other similar information. Cookies are necessary for proper function and use of Reliht. As stated above, we will never sell your information, even information collected from cookies.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Use and Disclosure of Your Personal Information
        <Typography className={classes.indentSpacer}>
          Reliht will collect and use your Personal Information primarily for the purpose of providing you with its services. Your Personal Information may also be used to communicate updates or recommendations. Reliht may contact you to conduct market research and/or surveys in an effort to continually improve our services. To unsubscribe from these email communications, Users may follow the unsubscribe instructions included in emails.
          <br />
          <b>
            Any content that you make available publicly, or share with others is done so at your own risk. Any Personal Information that is posted or sent by any other Users is your sole responsibility and Reliht will not be held responsible for any personal information that you make public.
          </b>
          <br />
          <b>Reliht</b> may use and disclose your Personal Information in the case of a sale, merger, consolidation, or acquisition. In that event, any acquirer will be subject to our obligations under this Privacy Policy, until such time as a change to the Privacy Policy is posted.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Accuracy
        <Typography className={classes.indentSpacer}>
          We do our best to keep your data accurate and up-to-date, to the extent that you provide us with the information we need to do that. If your data changes (like a new email address), then you're responsible for updating it on the site, and/or notifying us of those changes.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Public Information
        <Typography className={classes.indentSpacer}>
          <b>Blog:</b> We may have a public blog on our Website/App. Any information you include in a comment on our blog may be read, collected, and used by anyone. If your Personal Information appears on our blogs and you'd like it to be removed, fill out our <a href="/connect">connect</a> form. If we're not able to remove your information, we'll let you know why. In such a case, Reliht is not responsible or liable for anyone else’s collection or use of your Personal Information you published on our blogs, so consider what Personal Information you want available to the public before including it in any blog comment.
          <br />
          <b>Social Media Widgets:</b> Our Website may include social media features, like the Facebook Like button. These features may collect information about your IP address and which page you're visiting on our site, and they may set a cookie to make sure the feature functions properly. Social media features and widgets are either hosted by a third party or hosted directly on our site. Your interactions with those features are governed by the privacy policies of the companies that provide them.
        </Typography>
      </Typography>

      <Typography variant="h5" color="primary" className={classes.spacer}>
        Security
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Notice of Breach of Security
        <Typography className={classes.indentSpacer}>
          While security is of paramount importance to us, nobody can guarantee absolute protection from hackers. If a security breach causes an unauthorized intrusion into our system that materially affects you, then Reliht will notify you as soon as possible and later report the action we took in response.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Safeguarding Your Information and Content
        <Typography className={classes.indentSpacer}>
          Reliht registrations require a username and password to login. You must keep your username and password secure, and never disclose it to a third party. Passwords are encrypted, which means we can't see your passwords. We can't resend forgotten passwords either. We'll only be able to allow you to reset them.
          Any unauthorized reading or sharing of User's content or Personal Data is strictly forbidden by Reliht, Reliht employees, Reliht contractors or Reliht volunteers.
        </Typography>
      </Typography>

      <Typography variant="h5" color="primary" className={classes.spacer}>
        Website Use and Minors
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Must Be 18 to Open an Account
        <Typography className={classes.indentSpacer}>
          Reliht does not accept registrations from users who are under 18 years of age. <b>By registering with Reliht, you represent that you are at least 18 years old.</b>
          <br />
          A parent or legal guardian may be permitted to open an account in the name of the parent for use by their child if the child is at least 13 years old, but the parent is fully responsible for that account and all activity on the account.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        No Children Under Age 13 May Use a Parent's Account
        <Typography className={classes.indentSpacer}>
          You must be at least 13 years of age to use your parent's account at Reliht. Users who are found to be less than 13 years of age may be suspended without notice. Any content that is determined to have been posted by an account user who was under 13 at the time of posting will also be deleted. We will not knowingly collect personal information from children under the age of 13, and any information inadvertently gathered will be promptly deleted as soon as is reasonably possible after we are notified in writing of the collection of such information.
        </Typography>
      </Typography>

      <Typography variant="h6" className={classes.indentSpacer}>
        Representations and Certification of Parents
        <Typography className={classes.indentSpacer}>
          By registering an account and using the site, you <u>represent and certify</u> that you are 18 years of age or older. If you are allowing an account to be used by your 13 to 17-year-old child, you agree to be responsible for the account and all activity on the account, and you certify that your child is not younger than 13. You also assume all responsibility for monitoring your child's activity.
        </Typography>
      </Typography>

      <Typography align="right">
        G105878
      </Typography>
    </Grid>
  );
}
